import { useCallback, useMemo, useState } from 'react';

import { getProviderIcon } from '@/lib/auth/provider-icons';
import { Button } from '@/lib/base/buttons/button';
import { BodyText } from '@/lib/base/text/typography';
import { logger } from '@/lib/logger';

import { useConfigInfo } from '../../allauth_auth';
import { Client, redirectToProvider, settings } from '../../lib/allauth';
import type { Provider } from '../../types/allauth';

function ProviderButton(props: {
  provider: Provider;
  label: string;
  isPreparing: boolean;
  isDisabled: boolean;
  onSelect: (provider: Provider) => void;
}) {
  const { provider, label, isPreparing, isDisabled, onSelect } = props;
  const handleClick = useCallback(
    () => onSelect(provider),
    [onSelect, provider]
  );

  return (
    <Button
      className='w-full'
      onClick={handleClick}
      isLoading={isPreparing}
      showTextWhileLoading
      isDisabled={isDisabled}
      iconLeading={!isPreparing ? getProviderIcon(provider.id) : undefined}
      color='secondary'
    >
      <BodyText>{label}</BodyText>
    </Button>
  );
}

export default function ProviderList(props: {
  callbackURL: string;
  process: string;
  actionLabel?: string;
  // Full control over a button's label when "<actionLabel> <provider.name>"
  // isn't the wording wanted (e.g. product names like "Google Workspace").
  getLabel?: (provider: Provider) => string;
  // Disables every button — e.g. until a captcha challenge has been passed.
  isDisabled?: boolean;
  // Optional async step run before redirecting to the provider — e.g. binding an
  // invitation to the session. If it rejects, the redirect is aborted and the
  // rejection message is passed to onError.
  beforeRedirect?: (provider: Provider) => Promise<void>;
  onError?: (message: string) => void;
}) {
  const { callbackURL, process, getLabel, beforeRedirect, onError } = props;
  const actionLabel = props.actionLabel ?? 'Continue with';
  const isDisabled = props.isDisabled ?? false;
  const { providers: allProviders } = useConfigInfo();
  const [preparingId, setPreparingId] = useState<string | null>(null);

  const providers: Provider[] = useMemo(
    () => allProviders.filter((p: Provider) => p.client_id),
    [allProviders]
  );

  const handleSelect = useCallback(
    async (provider: Provider) => {
      if (beforeRedirect) {
        setPreparingId(provider.id);
        try {
          await beforeRedirect(provider);
        } catch (error) {
          setPreparingId(null);
          logger.error(
            'ProviderList: beforeRedirect failed',
            error instanceof Error ? error : new Error(String(error))
          );
          onError?.("We couldn't prepare sign-in. Try again.");
          return;
        }
      }
      // redirectToProvider navigates away; the clicked button stays in its
      // loading state until the browser leaves the page.
      // @ts-expect-error - process is a string
      redirectToProvider(provider.id, callbackURL, process);
    },
    [beforeRedirect, onError, callbackURL, process]
  );

  if (!providers.length) {
    return null;
  }

  return settings.client === Client.BROWSER ? (
    // onMouseDown/onPointerDown preventDefault prevents the browser from moving
    // focus away from the currently focused form field (e.g. email with
    // autoFocus) when the user clicks a provider button. Without this, the
    // blur-validation on the form field fires and shows "Required" errors even
    // though the user chose to log in via OAuth.
    <div
      className='flex flex-col gap-3'
      onMouseDown={e => e.preventDefault()}
      onPointerDown={e => e.preventDefault()}
    >
      {providers.map(provider => (
        <ProviderButton
          key={provider.id}
          provider={provider}
          label={
            getLabel ? getLabel(provider) : `${actionLabel} ${provider.name}`
          }
          isPreparing={preparingId === provider.id}
          isDisabled={
            isDisabled || (preparingId !== null && preparingId !== provider.id)
          }
          onSelect={handleSelect}
        />
      ))}
    </div>
  ) : null;
}
